import { Add } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import {
  Calendar,
  CalendarEvents,
  ClassOrTeacherBusyType,
} from '../../components/Calendar';
import { CalendarEventForm } from '../../components/CalendarEvent';
import { TeacherBusyEventForm } from '../../components/CalendarEvent/TeacherBusyEventForm';
import { CheckboxesTags, Option } from '../../components/Inputs/Checkboxes';
import { PageWrapper } from '../../components/PageWrapper';
import { Select } from '../../components/Select';
import { useAppSelector } from '../../hooks';
import { apiService } from '../../services/api';
import { ClassEvent, UserRes, UserRole } from '../../services/api.types';
import { teacherBusyService } from '../../services/teacher-busy.service';
import { TeacherBusyModel } from '../../services/teacher-busy.types';
import { usersService } from '../../services/users.service';
import { colors, neutralColors } from '../../utils/colors';
import {
  translateClassRoleShortcode,
  translateUserRoles,
} from '../../utils/translate-enums';

interface CalendarFormValues {
  users: Option[];
}

export enum ModeCalendar {
  create,
  edit,
  view,
}

const copy = { arr: colors.slice(0) };

function randomNoRepeats() {
  if (copy.arr.length < 1) {
    copy.arr = colors.slice(0);
  }
  const index = Math.floor(Math.random() * copy.arr.length);
  const item = copy.arr[index];
  copy.arr.splice(index, 1);
  return item;
}

export const ManyCalendarPage = () => {
  const calendarFormValues: CalendarFormValues = {
    users: [],
  };

  const forms = useForm<CalendarFormValues>({
    mode: 'all',
    defaultValues: calendarFormValues,
  });
  const selectUsers = forms.watch('users');

  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const isTeacher = user?.role === UserRole.TEACHER;
  const showCalendarCheckboxes =
    isAdminOrEmployee || user?.role === UserRole.RESPONSIBLE;
  const showCalendarFilter = isAdminOrEmployee;
  const showCalendarCreateButton = isAdminOrEmployee || isTeacher;

  const [filterCalendarOption, setFilterCalendarOption] = useState('');
  const [userEventsCache, setUserEventsCache] = useState<
    Record<number, ClassOrTeacherBusyType[]>
  >({});
  const [filteredEvents, setFilteredEvents] = useState<ClassEvent[]>([]);
  const [teacherBusyEvents, setTeacherBusyEvents] = useState<
    TeacherBusyModel[]
  >([]);
  const [eventsColors, setEventsColors] = useState<Record<number, string>>({});
  const [openClassEventModal, setOpenClassEventModal] = useState(false);
  const [openTeacherBusyEventModal, setOpenTeacherBusyEventModal] =
    useState(false);
  const [allUser, setAllUser] = useState<UserRes[]>([]);
  const [classEventInfo, setClassEventInfo] = useState<{
    mode: ModeCalendar;
    event: CalendarEvents | null;
  }>({ mode: ModeCalendar.create, event: null });
  const [dateNavigate, setDateNavigate] = useState(new Date());
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false);
  const clickRef = useRef(250);

  useEffect(() => {
    if (openClassEventModal || openTeacherBusyEventModal) return;

    const updatedEventsColors = { ...eventsColors };
    const newColors = [];
    for (const key in eventsColors) {
      if (!selectUsers.find((user) => user.id === Number(key))) {
        newColors.push(eventsColors[key]);
        delete updatedEventsColors[key];
      }
    }
    setEventsColors(updatedEventsColors);
    copy.arr.push(...newColors);

    async function fetchEventsForUser(user: Option) {
      let color = eventsColors[Number(user.id)];
      let userEvents = userEventsCache[Number(user.id)];
      if (!color) {
        color = randomNoRepeats();
        setEventsColors((prevColors) => ({ ...prevColors, [user.id]: color }));
      }
      if (!userEvents) {
        const start = dayjs(dateNavigate)
          .startOf('months')
          .add(-15, 'days')
          .toDate();
        const end = dayjs(dateNavigate)
          .endOf('months')
          .add(15, 'days')
          .toDate();
        if (
          UserRole.STUDENT === user.role ||
          UserRole.RESPONSIBLE === user.role
        ) {
          userEvents = await apiService
            .getAllEventStudent(Number(user.id), start, end)
            .then((res) =>
              res.map((event) => ({ ...event, userRoleCalendar: user.role }))
            )
            .catch(() => []);
        } else if (user.role === UserRole.TEACHER) {
          const classEvents = await apiService
            .getAllEventTeacher(Number(user.id), start, end)
            .then((res) =>
              res.map((event) => ({ ...event, userRoleCalendar: user.role }))
            )
            .catch(() => []);
          const busyEvents = await teacherBusyService
            .findAll(Number(user.id), start, end)
            .catch(() => []);
          // Assign gray color to busy events
          userEvents = [
            ...classEvents,
            ...busyEvents.map((event) => ({ ...event, color: '#bdbdbd' })),
          ];
        }
        setUserEventsCache((prevEventsUsers) => ({
          ...prevEventsUsers,
          [user.id]: userEvents,
        }));
      }
      // Assign gray color to non-class events for all roles
      const colorEvent = userEvents.map((event) =>
        event.isClass ? { ...event, color } : { ...event, color: '#bdbdbd' }
      );
      // Ensure class events never get a gray/black/white color
      // If color is gray/black/white, reassign a color from the palette
      const safeColorEvent = colorEvent.map((event) => {
        const neutralColorsLowercase = neutralColors.map((c) =>
          c.toLowerCase()
        );
        if (
          event.isClass &&
          neutralColorsLowercase.includes(event.color.toLowerCase() || '')
        ) {
          // Reassign a palette color
          return { ...event, color };
        }
        return event;
      });
      return { [user.id]: safeColorEvent };
    }

    async function fetchAllEvents() {
      const userEventsPromises = selectUsers.map(fetchEventsForUser);
      const userEventsArray = await Promise.all(userEventsPromises);
      setDisplayCalendar(!showCalendarCheckboxes || userEventsArray.length > 0);
      return userEventsArray.reduce(
        (result, userEvents) => ({ ...result, ...userEvents }),
        {}
      );
    }

    async function updateFilteredEvents() {
      const allEventsByUser = await fetchAllEvents();
      const allEvents = Object.values(allEventsByUser).flat();

      const classes = allEvents.filter((e) => e.isClass) as ClassEvent[];
      const teacherBusies = allEvents.filter(
        (e) => !e.isClass
      ) as TeacherBusyModel[];

      setFilteredEvents(classes);
      setTeacherBusyEvents(teacherBusies);
    }

    updateFilteredEvents();
    // userEventsCache is not on hook deps to avoid rendering loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectUsers,
    openClassEventModal,
    openTeacherBusyEventModal,
    dateNavigate,
  ]);

  const createEventLabel = useMemo(() => {
    if (isTeacher) return 'Marcar novos horários indisponíveis';
    return 'Criar novo Evento';
  }, [isTeacher]);

  const calendarOptions: Option[] = useMemo(() => {
    if (!user) return [];

    if (user.role === UserRole.RESPONSIBLE) {
      return allUser.map((u) => ({
        id: u.id,
        label: `${u.fullName}`,
        role: u.role,
      }));
    }

    return allUser
      .filter((u) =>
        filterCalendarOption === 'Todos' || filterCalendarOption === ''
          ? u.role === UserRole.STUDENT ||
            u.role === UserRole.TEACHER ||
            (u.role === UserRole.RESPONSIBLE && u.responsible?.isStudent)
          : filterCalendarOption ===
            translateUserRoles(u.role, u.responsible?.isStudent)
      )
      .map((u) => ({
        id: u.id,
        label: `${translateClassRoleShortcode(u.role)} - ${u.fullName}`,
        role: u.role,
      }));
  }, [allUser, filterCalendarOption, user]);

  useEffect(() => {
    if (!user) return;

    if (isAdminOrEmployee) {
      usersService
        .getAllUsers({
          role: [UserRole.STUDENT, UserRole.RESPONSIBLE, UserRole.TEACHER],
        })
        .then((res) => {
          setAllUser(res.users);
        });
    } else if (user.role === UserRole.RESPONSIBLE) {
      apiService.getAllResponsibleStudents(user.id).then((res) => {
        setAllUser(res.users);
      });
    } else {
      setAllUser([
        { ...user, teacher: null, student: null, responsible: null },
      ]);
      forms.setValue('users', [
        { id: user.id, label: user.fullName, role: user.role },
      ]);
    }
  }, [user]);

  const handleOpen = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(true);
    }, 150);
  };

  const handleClose = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(false);
    }, 100);
  };

  const onSelectEvent = useCallback(
    (eventInfo: CalendarEvents) => {
      if (!user) return;

      const actualDay = dayjs(eventInfo.start).toDate();
      let modeView: ModeCalendar;
      if (eventInfo.isClass) {
        if (user.role === UserRole.ADMIN) {
          modeView = ModeCalendar.edit;
        } else if (user.role === UserRole.EMPLOYEE) {
          const alreadyStartedOrFinished = dayjs().isAfter(eventInfo.start);
          modeView = alreadyStartedOrFinished
            ? ModeCalendar.view
            : ModeCalendar.edit;
        } else {
          modeView = ModeCalendar.view;
        }
      } else {
        modeView = isTeacher ? ModeCalendar.edit : ModeCalendar.view;
      }

      setClassEventInfo({
        mode: modeView,
        event: {
          ...eventInfo,
          startDate: actualDay,
          startHour: eventInfo.start,
        },
      });

      handleOpen(
        eventInfo.isClass
          ? setOpenClassEventModal
          : setOpenTeacherBusyEventModal
      );
    },
    [user]
  );

  const updateEvent = (
    eventsToInsert: ClassOrTeacherBusyType[],
    eventsToRemove: ClassOrTeacherBusyType[]
  ) => {
    // this function assumes that teacher and student are the same for both events

    const updateState = (events: ClassOrTeacherBusyType[]) => {
      const filteredEvents = events.filter(
        (e) => !eventsToRemove.find((r) => r.id === e.id)
      );
      return [...filteredEvents, ...eventsToInsert];
    };

    const refEvent = eventsToInsert[0] || eventsToRemove[0];

    setUserEventsCache((prevEventsUsers) => {
      const newEventsUsers = { ...prevEventsUsers };

      const teacherId: number | undefined = refEvent?.teacherId;
      if (teacherId) {
        newEventsUsers[teacherId] = updateState(
          newEventsUsers[teacherId] || []
        );
      }

      if (refEvent?.isClass) {
        const studentId: number | undefined = refEvent?.studentId;
        if (studentId) {
          newEventsUsers[studentId] = updateState(
            newEventsUsers[studentId] || []
          );
        }
      }

      return newEventsUsers;
    });
  };

  const onCreate = (event: ClassOrTeacherBusyType) => {
    updateEvent([event], []);
  };

  const onUpdate = async (
    oldEvent: ClassOrTeacherBusyType,
    newEvent: ClassOrTeacherBusyType
  ) => {
    const eventsToRemove = [oldEvent];
    const eventsToInsert = [newEvent];
    if (oldEvent.id !== newEvent.id) {
      let oldEventUpdated: ClassOrTeacherBusyType | undefined;
      if (oldEvent.isClass) {
        oldEventUpdated = await apiService
          .getEvent(oldEvent.id)
          .catch(() => undefined);
      } else {
        oldEventUpdated = await teacherBusyService
          .findOne(oldEvent.id)
          .catch(() => undefined);
      }
      if (oldEventUpdated) eventsToInsert.push(oldEventUpdated);
    }

    updateEvent(eventsToInsert, eventsToRemove);
  };

  const onRemove = async (event: ClassOrTeacherBusyType) => {
    let eventUpdated: ClassOrTeacherBusyType | undefined;
    if (event.isClass) {
      eventUpdated = await apiService.getEvent(event.id).catch(() => undefined);
    } else {
      eventUpdated = await teacherBusyService
        .findOne(event.id)
        .catch(() => undefined);
    }

    const eventsToRemove = [event];
    const eventsToInsert = eventUpdated ? [eventUpdated] : [];
    updateEvent(eventsToInsert, eventsToRemove);
  };

  const onChangeDate = (date: Date) => {
    setDateNavigate(date);
    setUserEventsCache({});
  };

  return (
    <PageWrapper>
      <div className="flex flex-col w-full h-full">
        <FormProvider {...forms}>
          <div
            className={clsx(
              'w-full m-0 mb-3',
              'flex flex-col lg:flex-row gap-4',
              { 'justify-end': !showCalendarCheckboxes }
            )}
          >
            {showCalendarCheckboxes && (
              <div className="w-full">
                <CheckboxesTags
                  name="users"
                  label="Selecionar agendas"
                  startAdornment={<SearchIcon />}
                  placeholder={
                    displayCalendar
                      ? 'Usuários selecionados'
                      : 'Sem usuários selecionados'
                  }
                  colors={eventsColors}
                  options={calendarOptions}
                />
              </div>
            )}

            <div
              className={clsx(
                'flex flex-wrap lg:flex-nowrap gap-4',
                showCalendarFilter ? 'justify-between' : 'justify-end',
                { hidden: !showCalendarFilter && !showCalendarCreateButton }
              )}
            >
              {showCalendarFilter && (
                <div className="flex min-w-max items-end">
                  <Select
                    id="Select"
                    size="small"
                    color="secondary"
                    label="Filtrar por cargo"
                    value={filterCalendarOption}
                    onChange={(value: string) => {
                      forms.setValue('users', []);
                      setFilterCalendarOption(value);
                    }}
                    className={clsx('w-40 font-semibold text-left')}
                    options={[
                      { value: 'Todos', label: 'Todos' },
                      { value: 'Estudante', label: 'Estudante' },
                      {
                        value: 'Responsável/Estudante',
                        label: 'Responsável/Estudante',
                      },
                      { value: 'Professor', label: 'Professor' },
                    ]}
                  />
                </div>
              )}

              {showCalendarCreateButton && (
                <div className="flex items-end w-max">
                  <Button
                    circular
                    startIcon={<Add />}
                    onClick={() => {
                      setClassEventInfo({
                        mode: ModeCalendar.create,
                        event: null,
                      });
                      handleOpen(
                        !isTeacher
                          ? setOpenClassEventModal
                          : setOpenTeacherBusyEventModal
                      );
                    }}
                  >
                    {createEventLabel}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </FormProvider>

        <div className="h-0 flex-grow">
          {displayCalendar && (
            <Calendar
              events={filteredEvents}
              teacherBusyEvents={teacherBusyEvents}
              date={dateNavigate}
              onDateChange={onChangeDate}
              onSelectEvent={onSelectEvent}
            />
          )}

          {openClassEventModal && (
            <CalendarEventForm
              eventInfo={{
                ...classEventInfo,
                event: classEventInfo.event?.isClass
                  ? classEventInfo.event
                  : null,
              }}
              open={openClassEventModal}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onCreate={onCreate}
              onClose={() => handleClose(setOpenClassEventModal)}
            />
          )}

          {openTeacherBusyEventModal && (
            <TeacherBusyEventForm
              eventInfo={classEventInfo}
              open={openTeacherBusyEventModal}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onCreate={onCreate}
              onClose={() => handleClose(setOpenTeacherBusyEventModal)}
            />
          )}
        </div>
      </div>
    </PageWrapper>
  );
};
